/**
 * EarlyBookingNotice Component
 *
 * The body of the "Are you sure about this time?" prompt shown before a booking
 * that starts between 1:00 and 5:59 AM club time, a time members misread as PM.
 * Icon, title, the AM/PM call-out and the selected range; the surface around it
 * (bottom sheet, dialog) and its buttons belong to the caller.
 *
 * `titleAs` / `descriptionAs` take a component, not just a tag, so a dialog can
 * pass its own Title / Description parts and stay labelled for screen readers.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/alertdialog/
 */

import type { ComponentPropsWithoutRef, ElementType } from 'react';
import { ClockIcon } from '#icons';
import { cn } from '#utils';
import { formatPmCounterpart, formatTime, formatTimeRange } from '@butter/library/datetime';

const TITLE = 'Are you sure about this time?';
const SUBTITLE = 'Please confirm your early morning booking.';

export interface EarlyBookingNoticeProps extends ComponentPropsWithoutRef<'div'> {
  /** Club-local slot start, "HH:mm". */
  startTime: string;
  /** Club-local slot end, "HH:mm". */
  endTime: string;
  /** Element or component for the title. */
  titleAs?: ElementType;
  /** Element or component for the subtitle and AM/PM call-out. Must accept phrasing content. */
  descriptionAs?: ElementType;
}

export function EarlyBookingNotice({
  startTime,
  endTime,
  titleAs: Title = 'h2',
  descriptionAs: Description = 'p',
  className,
  ...props
}: EarlyBookingNoticeProps) {
  const pmLabel = formatPmCounterpart(startTime);

  return (
    <div className={cn('flex flex-col items-center gap-6 text-center', className)} {...props}>
      <div className="flex size-18 items-center justify-center rounded-full border border-theme-100 bg-gradient-to-b from-white to-theme-100">
        <ClockIcon aria-hidden="true" className="size-10 text-theme-600" strokeWidth={1.2} />
      </div>
      <div className="flex w-full flex-col gap-5">
        <div className="flex flex-col gap-1">
          <Title className="text-xl font-semibold leading-tight text-butter-gray-900">{TITLE}</Title>
          <Description className="text-base font-medium text-butter-gray-600">
            {SUBTITLE}
            {pmLabel && (
              <span className="mt-1 block" data-testid="early-booking-emphasis">
                This booking starts at{' '}
                <strong className="font-semibold text-butter-gray-900">{formatTime(startTime)}</strong>, not {pmLabel}.
              </span>
            )}
          </Description>
        </div>
        <div className="flex w-full items-center justify-between rounded-18 border border-butter-gray-200 bg-butter-gray-25 p-4.5 text-left">
          <span className="text-base font-medium text-butter-gray-700">Selected time</span>
          <span className="text-base font-semibold text-butter-gray-900" data-testid="early-booking-selected-time">
            {formatTimeRange(startTime, endTime)}
          </span>
        </div>
      </div>
    </div>
  );
}
